import type { SpacePath } from "@/components/main/stores/orgContextStore";

export interface FilteredSpaceRow {
  path: SpacePath;
  /// False for a parent kept only so its match below has its path.
  isMatch: boolean;
  /// A kept row follows directly under it.
  openBelow: boolean;
}

export interface SpaceFilterResult {
  rows: FilteredSpaceRow[];
  /// Match ids in tree order: what ↑ ↓ step through and ↵ opens.
  matches: string[];
  /// For `highlightSegments`, which needs a global pattern.
  pattern: RegExp;
}

/// The tree narrowed to the spaces whose name contains `query`, case aside as
/// in the space picker, each with every parent above it, in tree order. Null
/// for a blank query: the whole tree shows.
export function filterSpaces(
  paths: SpacePath[],
  query: string | null,
): SpaceFilterResult | null {
  const needle = query?.trim().toLowerCase();
  if (!needle) return null;
  const depth = (i: number) => paths[i].ancestors.length;
  const kept = new Map<number, boolean>();
  paths.forEach((path, i) => {
    if (!path.space.name.toLowerCase().includes(needle)) return;
    kept.set(i, true);
    // A subtree follows its root, so each parent is the nearest row above one level up.
    for (let want = depth(i) - 1, j = i - 1; want >= 0 && j >= 0; j--) {
      if (depth(j) !== want) continue;
      if (!kept.has(j)) kept.set(j, false);
      want--;
    }
  });
  const order = [...kept.keys()].sort((a, b) => a - b);
  const rows = order.map((i, k) => ({
    path: paths[i],
    isMatch: kept.get(i)!,
    openBelow: k + 1 < order.length && depth(order[k + 1]) > depth(i),
  }));
  return {
    rows,
    matches: rows.filter((r) => r.isMatch).map((r) => r.path.space.space_id),
    pattern: new RegExp(needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
  };
}
